import React, { useState, useEffect } from "react";
import { FaTimes, FaSpinner, FaCheck } from "react-icons/fa";
import { 
  FiCompass, 
  FiTrendingUp, 
  FiMapPin, 
  FiEye, 
  FiEyeOff, 
  FiDownload, 
  FiFileText,
  FiChevronRight,
  FiChevronDown
} from "react-icons/fi";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";  // new import

// Update base URLs to point to the main directory instead of subfolders
const BASE_VGA = "https://romaneio.salexpress.com.br/roterizacao/";
const BASE_POA = "https://romaneio.salexpress.com.br/roterizacao/";
const BASE_DIE = "https://romaneio.salexpress.com.br/roterizacao/";
const BASES = { VGA: BASE_VGA, POA: BASE_POA, DIE: BASE_DIE };

const JsonViewer = ({ data }) => {
  if (data === null) return <span>Carregando JSON...</span>;
  if (typeof data !== "object") return <span>{String(data)}</span>;
  if (Array.isArray(data)) return <JsonArray data={data} />;
  return (
    <div className="ml-4">
      {Object.keys(data).map((key) => (
        <div key={key}>
          <strong>{key}: </strong>
          <JsonViewer data={data[key]} />
        </div>
      ))}
    </div>
  );
};

const JsonArray = ({ data }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="bg-gray-200 dark:bg-dark-card px-2 py-1 rounded text-text dark:text-dark-text">
        Array [{data.length}] {open ? "[-]" : "[+]"}
      </button>
      {open && (
        <div className="ml-4">
          {data.map((item, index) => (
            <div key={index}>
              <JsonViewer data={item} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GroupedRoutesDropdown = ({ groupedRoutes }) => {
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = (groupKey) => {
    setOpenPanel((prev) => (prev === groupKey ? null : groupKey));
  };
  const openPopup = (url) => {
    window.open(url, "_blank", "width=800,height=600,scrollbars=yes");
  };
  const getProgressBarColor = (percent) => {
    if (percent <= 70) return "bg-green-500";
    if (percent <= 95) return "bg-orange-500";
    return "bg-red-500";
  };
  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  if (!groupedRoutes) return <p className="text-text dark:text-dark-text">No routes available.</p>;
  return (
    <div className="space-y-4">
      {Object.entries(groupedRoutes).map(([groupKey, groupData]) => {
        // Obtenha as cidades do subRoute "Veiculo 1" se existir; senão, caia para cidadesRota
        const cities =
          groupData.subRoutes && groupData.subRoutes["Veiculo 1"] && groupData.subRoutes["Veiculo 1"].cidades
            ? groupData.subRoutes["Veiculo 1"].cidades
            : groupData.cidadesRota;
        return (
          <div key={groupKey} className="border dark:border-dark-border rounded-2xl shadow dark:shadow-dark-surface/20 p-4 cursor-pointer bg-white-whither dark:bg-dark-surface" onClick={() => togglePanel(groupKey)}>
            <div className="flex justify-between items-center">
              <button className="text-left font-bold text-lg text-text dark:text-dark-text">
                {groupKey} {openPanel === groupKey ? "▼" : "►"}
              </button>
              <div className="relative group overflow-visible">
                <button
                  onClick={() => openPopup(groupData.rotaLink)}
                  className="bg-green-500 text-white-whiter p-2 rounded-full hover:bg-green-600 focus:outline-none"
                >
                  <FiMapPin size={18} />
                </button>
                <span className="absolute right-full ml-2 whitespace-nowrap px-2 py-1 rounded-md bg-gray-800 dark:bg-dark-card text-white-whiter dark:text-dark-text z-50 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Ver Rota
                </span>
              </div>
            </div>
            {/* Usa as cidades do subRoute "Veiculo 1" */}
            {cities && cities.length > 0 && (
              <p className="mt-1 text-sm text-gray-600 dark:text-dark-textSecondary">
                {cities.join(" ➜ ")}
              </p>
            )}
            {openPanel === groupKey && (
              <div className="mt-2 pl-4">
                <div className="mt-8 space-y-2">
                  {groupData.subRoutes && (
                    <div className="mt-2 space-y-2">
                      {Object.entries(groupData.subRoutes).map(([vehicle, details]) => (
                        <div key={vehicle} className="border-l-4 border-blue-500 pl-4 mb-4">
                          {/* Informações do veículo em cartão */}
                          <div className="bg-gray-100 dark:bg-dark-card p-3 rounded-lg shadow-sm mb-2">
                            <div className="flex justify-between items-center">
                              <h3 className="text-lg font-semibold text-text dark:text-dark-text">{details.veiculosSelecionados[0]?.perfil || vehicle}</h3>
                              <div className="flex items-center">
                                <div className="w-24 h-3 bg-gray-300 dark:bg-dark-border rounded-full overflow-hidden mr-2">
                                  <div
                                    className={`h-full ${getProgressBarColor(details.percentCheio)}`}
                                    style={{ width: `${details.percentCheio}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm text-gray-700 dark:text-dark-textSecondary">{details.percentCheio}%</span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-dark-textSecondary mt-1">Lucro: {formatCurrency(details.profit)}</p>
                          </div>
                          {/* Grade de informações adicionais */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm mb-2">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-700 dark:text-dark-text">Tempo</span>
                              <span className="text-gray-600 dark:text-dark-textSecondary">{details.tempoRotaHoras} hrs</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-700 dark:text-dark-text">Peso</span>
                              <span className="text-gray-600 dark:text-dark-textSecondary">{details.totalWeight.toFixed(2)} kg</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-700 dark:text-dark-text">Entregas</span>
                              <span className="text-gray-600 dark:text-dark-textSecondary">{details.numEntregas}</span>
                            </div>
                          </div>
                          {/* Veículos Selecionados */}
                          {details.veiculosSelecionados && (
                            <div className="mt-3 space-y-2">
                              {details.veiculosSelecionados.map((veiculo, idx) => (
                                <div key={idx} className="bg-gray-100 dark:bg-dark-card p-2 rounded-lg shadow flex justify-between items-center text-sm">
                                  <span className="text-text dark:text-dark-text">Cap: {veiculo.capacidade_maxima_kg} kg</span>
                                  <span className="text-text dark:text-dark-text">R$ {veiculo.valor_km.toFixed(2)}/km</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {details.deliveries && details.deliveries.length > 0 && (
                            // Improved table styling for better readability and modern look
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border mt-2 text-xs bg-white-whither dark:bg-dark-surface">
                              <thead className="bg-gray-100 dark:bg-dark-card">
                                <tr>
                                  <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">CTRC</th>
                                  <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Cliente</th>
                                  <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Cidade</th>
                                  <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Peso (kg)</th>
                                  <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Frete (R$)</th>
                                  <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Volumes</th>
                                  <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Previsão</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white-whither dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
                                {details.deliveries.map((delivery, index) => (
                                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-card">
                                    <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.ctrc}</td>
                                    <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.cliente_recebedor}</td>
                                    <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.cidade}</td>
                                    <td className="px-2 py-1 text-text dark:text-dark-text">{roundValue(delivery.peso_real_kg)}</td>
                                    <td className="px-2 py-1 text-text dark:text-dark-text">{roundValue(delivery.valor_frete)}</td>
                                    <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.quantidade_volumes}</td>
                                    <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.previsao_date}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SummaryTable = ({ summary }) => {
  if (!summary) return <p className="text-text dark:text-dark-text">No summary info available.</p>;
  const keyMapping = {
    overallProfit: "Lucro Total",
    totalSomaFrete: "Frete Total",
    totalCustoKm: "Custo Total",
    totalAllDeliveries: "Qnt. Entregas",
    totalAllKm: "Quilometragem Total",
    overallWeight: "Peso Total",
    usedVehicles: "Veículos Usados",
  };
  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  const formatVehicles = (vehicles) =>
    Object.entries(vehicles)
      .map(([type, count]) => `${type}: <strong>${count}</strong>`)
      .join(", ");
  return (
    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border mt-4 text-sm bg-white-whither dark:bg-dark-surface">
      <thead className="bg-gray-100 dark:bg-dark-card">
        <tr>
          <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Descrição</th>
          <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Valor</th>
        </tr>
      </thead>
      <tbody className="bg-white-whither dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
        {Object.entries(summary).map(([key, value], index) => (
          <tr key={key} className={index % 2 === 0 ? "bg-gray-50 dark:bg-dark-card" : "bg-white-whither dark:bg-dark-surface"}>
            <td className="px-4 py-2 text-text dark:text-dark-text">
              <strong>{keyMapping[key] || key}</strong>
            </td>
            <td
              className="px-4 py-2 text-text dark:text-dark-text"
              dangerouslySetInnerHTML={{
                __html: ["overallProfit", "totalSomaFrete", "totalCustoKm"].includes(key)
                  ? formatCurrency(value)
                  : typeof value === "object" && key === "usedVehicles"
                  ? formatVehicles(value)
                  : value,
              }}
            ></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const DeliveriesTable = ({ deliveries }) => {
  if (!deliveries || deliveries.length === 0) return <p className="text-text dark:text-dark-text">No deliveries available.</p>;
  return (
    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border mt-2 text-sm bg-white-whither dark:bg-dark-surface">
      <thead className="bg-gray-100 dark:bg-dark-card">
        <tr>
          <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">CTRC</th>
          <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Cliente</th>
          <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Cidade</th>
          <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Peso (kg)</th>
          <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Frete (R$)</th>
          <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Volumes</th>
          <th className="px-2 py-1 text-left font-semibold text-gray-700 dark:text-dark-text">Previsão</th>
        </tr>
      </thead>
      <tbody className="bg-white-whither dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
        {deliveries.map((delivery, index) => (
          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-card">
            <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.ctrc}</td>
            <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.cliente_recebedor}</td>
            <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.cidade}</td>
            <td className="px-2 py-1 text-text dark:text-dark-text">{roundValue(delivery.peso_real_kg)}</td>
            <td className="px-2 py-1 text-text dark:text-dark-text">{roundValue(delivery.valor_frete)}</td>
            <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.quantidade_volumes}</td>
            <td className="px-2 py-1 text-text dark:text-dark-text">{delivery.previsao_date}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Nova função para formatar a data sem conversõesm conversões
function formatDate(dateString) {
  return `${dateString.substring(8,10)}/${dateString.substring(5,7)}/${dateString.substring(0,4)} ${dateString.substring(11,16)}`;
}

// Add helper function to round numbers to two decimals
function roundValue(value) {
  return typeof value === "number" ? Number(value).toFixed(2) : value;
}

const Rastreamento = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState(""); // new state for status text
  const [loadingComplete, setLoadingComplete] = useState(false); // new state for completion
  const [lastRun, setLastRun] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [finalJson, setFinalJson] = useState(null);
  const [showTotal, setShowTotal] = useState(true);
  const [showRotas, setShowRotas] = useState(true);
  const [viewModeSal, setViewModeSal] = useState(false);  // Adiciona novos estados para a versão Sal Express
  const [lastRunSal, setLastRunSal] = useState(null);
  const [finalJsonSal, setFinalJsonSal] = useState(null);
  const [lastRunDie, setLastRunDie] = useState(null);
  const [finalJsonDie, setFinalJsonDie] = useState(null);
  const [baseSelected, setBaseSelected] = useState("VGA");
  const [viewBase, setViewBase] = useState(null);  // "VGA" or "POA" or "DIE"
  const [timeSelectionVisible, setTimeSelectionVisible] = useState(false); // New state for time selection popup
  const [startTime, setStartTime] = useState(6); // Default to 6:00
  const [pendingBaseKey, setPendingBaseKey] = useState(null); // Store which base is pending for time selection

  useEffect(() => {
    // Update lastRun for Varginha
    fetch(BASE_VGA + "VGA/Resultado/lastRunSal.json") // updated URL
      .then((res) => res.text())
      .then((text) => {
        const jsonStr = text.replace(/^callback\((.*)\);?$/, "$1");
        const data = JSON.parse(jsonStr);
        setLastRun(formatDate(data.lastRun)); 
      })
      .catch((error) => console.error("Erro ao buscar lastRun:", error));
  }, []);

  // Update lastRunSal for Pouso Alegre
  useEffect(() => {
    fetch(BASE_POA + "POA/Resultado/lastRunSal.json") // updated URL
      .then((res) => res.text())
      .then((text) => {
        const jsonStr = text.replace(/^callback\((.*)\);?$/, "$1");
        const data = JSON.parse(jsonStr);
        setLastRunSal(formatDate(data.lastRunSal || data.lastRun));
      })
      .catch((error) => console.error("Erro ao buscar lastRunSal:", error));
  }, []);

  // Update lastRunDie for Divinópolis
  useEffect(() => {
    fetch(BASE_DIE + "DIE/Resultado/lastRunSal.json")
      .then((res) => res.text())
      .then((text) => {
        const jsonStr = text.replace(/^callback\((.*)\);?$/, "$1");
        const data = JSON.parse(jsonStr);
        setLastRunDie(formatDate(data.lastRunSal || data.lastRun));
      })
      .catch((error) => console.error("Erro ao buscar lastRunDie:", error));
  }, []);

  // Modified route generation with time selection
  const initiateRouteGeneration = (baseKey) => {
    setPendingBaseKey(baseKey);
    setStartTime(6); // Reset to default time
    setTimeSelectionVisible(true);
  };

  const handleRunRoutes = async (baseKey, startHour) => {
    setBaseSelected(baseKey);
    setModalVisible(true);
    setProgress(0);
    setLoadingComplete(false);
    const endpoints = [
      "cidadesEntregas(2).php",
      "buscarEntregas(4).php",
      "roterizadorSal.php",
      "rotasFinalizadasSal.php",
      "convertToCSVSal.php"
    ];
    // Define the status for each step
    const statusMapping = [
      "Buscando cidades",
      "Buscando entregas",
      "Roterizando",
      "Finalizando",
      "Gerando CSV"
    ];
    const baseUrl = BASES[baseKey];
    const opcaoMapping = { VGA: "VGA", POA: "POA" };
    const opcao = opcaoMapping[baseKey] || baseKey;
    for (let i = 0; i < endpoints.length; i++) {
      setLoadingStatus(statusMapping[i]);
      // Add the start time parameter to the URL
      const url = `${baseUrl}${endpoints[i]}?opcao=${opcao}&horario_inicio=${startHour}`;
      const res = await fetch(url, { method: "GET" });
      await res.text();
      setProgress(Math.round(((i + 1) / endpoints.length) * 100));
    }
    // Update lastRun after endpoints execute
    fetch(`${baseUrl}${baseKey}/Resultado/lastRunSal.json`)
      .then((r) => r.text())
      .then((txt) => {
        const data = JSON.parse(txt.replace(/^callback\((.*)\);?$/, "$1"));
        baseKey === "VGA" ? setLastRun(data.lastRun) : setLastRunSal(data.lastRunSal || data.lastRun);
      });
    setLoadingStatus("Sucesso!");
    setLoadingComplete(true);
    // Auto-close the popup after 5 seconds
    setTimeout(() => {
      setModalVisible(false);
      setLoadingStatus("");
      setLoadingComplete(false);
    }, 5000);
  };

  // Unified view
  const handleViewRoutes = (baseKey) => {
    setViewBase(baseKey);
    // Build the file path using the base key folder structure
    const file = `${baseKey}/Resultado/finalOutputSal.json`;
    fetch(`${BASES[baseKey]}${file}`)
      .then((r) => r.text())
      .then((txt) => {
        const data = JSON.parse(txt.replace(/^callback\((.*)\);?$/,"$1"));
        if (baseKey === "VGA") {
          setFinalJson(data);
        } else if (baseKey === "POA") {
          setFinalJsonSal(data);
        } else if (baseKey === "DIE") {
          setFinalJsonDie(data);
        }
      });
  };

  const handleDownloadSummaryImage = () => {
    const summaryTable = document.getElementById("summary-table-sal-express");
    if (summaryTable) {
      html2canvas(summaryTable).then((canvas) => {
        const link = document.createElement("a");
        const today = new Date().toISOString().split("T")[0]; // Formato YYYY-MM-DD
        link.download = `SumárioRotas(${today}).png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      });
    }
  };

  // New function to download summary as an Excel file
  const handleDownloadSummarySpreadsheet = () => {
    if (!finalJson || !finalJson.totalRotas) return;
    const todayStr = new Date().toLocaleDateString("pt-BR");
    // Header: data, nome, perfil, entregas, peso, frete, lucro (somaFrete), custo (custoKm)
    const data = [[ "data", "nome", "perfil", "entregas", "peso", "frete", "lucro", "custo" ]];
    
    Object.values(finalJson.totalRotas).forEach(route => {
      // Use route.total values if available, otherwise sum over all subRoutes
      const lucro =
        route.total && route.total.somaFrete
          ? route.total.somaFrete
          : Object.values(route.subRoutes || {}).reduce((acc, sr) => acc + (sr.somaFrete || 0), 0);
      const custo =
        route.total && route.total.custoKm
          ? route.total.custoKm
          : Object.values(route.subRoutes || {}).reduce((acc, sr) => acc + (sr.custoKm || 0), 0);
          
      data.push([
        todayStr,
        route.nome,
        route.veiculos,
        roundValue(route.total.entregas),
        roundValue(route.total.peso),
        roundValue(route.total.frete),
        roundValue(lucro),
        roundValue(custo)
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sumário");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const today = new Date().toISOString().split("T")[0];
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Sumario_${today}.xlsx`;
    link.click();
  };

  // Default view
  if (!viewBase) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-5 bg-white-whiter dark:bg-dark-bg space-y-8">
        <div className="flex flex-row justify-center items-start space-x-8">
          {/* Card para Varginha */}
          <div className="border dark:border-dark-border rounded-xl shadow-xl dark:shadow-dark-surface/20 overflow-hidden w-full max-w-sm bg-white-whither dark:bg-dark-surface">
            {/* Capa com imagem */}
            <img src={process.env.PUBLIC_URL + '/icons/vga.png'} alt="Varginha" className="w-full h-32 object-cover" />
            <div className="p-3">
              <h2 className="text-2xl font-bold mb-3 text-text dark:text-dark-text">Varginha</h2>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => initiateRouteGeneration("VGA")}
                  className="flex items-center bg-blue-500 text-white-whiter font-bold py-2 px-3 rounded-full hover:bg-blue-600 focus:outline-none"
                >
                  <FiCompass className="mr-1" />
                  Roterização
                </button>
                <button
                  onClick={() => handleViewRoutes("VGA")}
                  className="flex items-center bg-green-500 text-white-whiter font-bold py-2 px-3 rounded-full hover:bg-green-600 focus:outline-none"
                >
                  <FiTrendingUp className="mr-1" />
                  Visualizar
                </button>
              </div>
              <p className="mt-3 text-xs text-right text-gray-500 dark:text-dark-textSecondary">
                {lastRun ? `Última rota realizada: ${lastRun}` : "Carregando última rota..."}
              </p>
            </div>
          </div>
          {/* Card para Pouso Alegre */}
          <div className="border dark:border-dark-border rounded-xl shadow-xl dark:shadow-dark-surface/20 overflow-hidden w-full max-w-sm bg-white-whither dark:bg-dark-surface">
            {/* Capa com imagem */}
            <img src={process.env.PUBLIC_URL + '/icons/poa.jpg'} alt="Pouso Alegre" className="w-full h-32 object-cover" />
            <div className="p-3">
              <h2 className="text-2xl font-bold mb-3 text-text dark:text-dark-text">Pouso Alegre</h2>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => initiateRouteGeneration("POA")}
                  className="flex items-center bg-blue-500 text-white-whiter font-bold py-2 px-3 rounded-full hover:bg-blue-600 focus:outline-none"
                >
                  <FiCompass className="mr-1" /> Roterização
                </button>
                <button
                  onClick={() => handleViewRoutes("POA")}
                  className="flex items-center bg-green-500 text-white-whiter font-bold py-2 px-3 rounded-full hover:bg-green-600 focus:outline-none"
                >
                  <FiTrendingUp className="mr-1" /> Visualizar
                </button>
              </div>
              <p className="mt-3 text-xs text-right text-gray-500 dark:text-dark-textSecondary">
                {lastRunSal ? `Última rota realizada: ${lastRunSal}` : "Carregando última rota..."}
              </p>
            </div>
          </div>
          {/* Card para Divinópolis */}
          <div className="border dark:border-dark-border rounded-xl shadow-xl dark:shadow-dark-surface/20 overflow-hidden w-full max-w-sm bg-white-whither dark:bg-dark-surface">
            {/* Capa com imagem */}
            <img src={process.env.PUBLIC_URL + '/icons/die.jpg'} alt="Divinópolis" className="w-full h-32 object-cover" />
            <div className="p-3">
              <h2 className="text-2xl font-bold mb-3 text-text dark:text-dark-text">Divinópolis</h2>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => initiateRouteGeneration("DIE")}
                  className="flex items-center bg-blue-500 text-white-whiter font-bold py-2 px-3 rounded-full hover:bg-blue-600 focus:outline-none"
                >
                  <FiCompass className="mr-1" /> Roterização
                </button>
                <button
                  onClick={() => handleViewRoutes("DIE")}
                  className="flex items-center bg-green-500 text-white-whiter font-bold py-2 px-3 rounded-full hover:bg-green-600 focus:outline-none"
                >
                  <FiTrendingUp className="mr-1" /> Visualizar
                </button>
              </div>
              <p className="mt-3 text-xs text-right text-gray-500 dark:text-dark-textSecondary">
                {lastRunDie ? `Última rota realizada: ${lastRunDie}` : "Carregando última rota..."}
              </p>
            </div>
          </div>
        </div>
        
        {/* Time Selection Modal */}
        {timeSelectionVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-[-100px] bg-gray-900 dark:bg-black opacity-60" 
                 onClick={() => setTimeSelectionVisible(false)}></div>
            <div className="relative dark:bg-dark-surface bg-white-whiter rounded-xl shadow-lg dark:shadow-dark-surface/20 p-6 w-96 max-w-full flex flex-col items-center">
              <h3 className="text-xl font-semibold text-text dark:text-dark-text mb-6">
                Horário de Início da Rota
              </h3>
              
              <div className="w-full mb-8">
                <div className="relative w-full h-16 flex items-center justify-center mb-2">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-blue-600/10 dark:from-blue-700/20 dark:to-blue-900/20 rounded-lg"></div>
                  <div className="relative z-10 text-center">
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 flex items-baseline">
                      <span>{startTime}</span>
                      <span className="text-xl ml-1">:00</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {startTime < 12 ? 'Manhã' : 'Tarde'}
                    </div>
                  </div>
                </div>

                <div className="w-full px-2">
                  <input 
                    type="range" 
                    min="6" 
                    max="17" 
                    value={startTime} 
                    onChange={(e) => setStartTime(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(startTime-6)*100/11}%, #e5e7eb ${(startTime-6)*100/11}%, #e5e7eb 100%)`,
                    }}
                  />
                  
                  <div className="relative w-full flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400 px-1">
                    <div>6:00</div>
                    <div>9:00</div>
                    <div>12:00</div>
                    <div>15:00</div>
                    <div>17:00</div>
                  </div>
                </div>
                
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  {[6, 8, 10, 12, 14, 16].map(hour => (
                    <button
                      key={hour}
                      onClick={() => setStartTime(hour)}
                      className={`px-3 py-1 rounded-full text-sm transition-all ${
                        startTime === hour 
                          ? 'bg-blue-500 text-white-whiter font-medium' 
                          : 'bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-dark-text'
                      }`}
                    >
                      {hour}:00
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex space-x-3 w-full">
                <button
                  onClick={() => setTimeSelectionVisible(false)}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-dark-text py-3 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setTimeSelectionVisible(false);
                    handleRunRoutes(pendingBaseKey, startTime);
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white-whiter py-3 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors font-medium"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
        
        {modalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-[-34px] bg-gray-900 dark:bg-black opacity-60"></div>
            <div className="relative bg-white-whither dark:bg-dark-surface rounded-lg shadow-lg dark:shadow-dark-surface/20 p-6 w-80 flex flex-col items-center space-y-4">
              {!loadingComplete ? (
                <>
                  <div className="w-full text-center text-xl font-semibold text-gray-800 dark:text-dark-text">
                    {loadingStatus || "Carregando..."}
                  </div>
                  <div className="w-full h-2 bg-gray-200 dark:bg-dark-border rounded-full">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-dark-textSecondary">
                    {progress}% concluído
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-green-600 dark:text-green-400">
                    <FaCheck size={48} />
                  </div>
                  <div className="text-xl font-semibold text-gray-800 dark:text-dark-text">
                    Operação concluída!
                  </div>
                  <button
                    onClick={() => {
                      setModalVisible(false);
                      setLoadingStatus("");
                      setLoadingComplete(false);
                    }}
                    className="mt-2 px-4 py-2 bg-green-500 text-white-whiter rounded hover:bg-green-600 focus:outline-none"
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (viewBase === "VGA") {
    return (
      <section className="p-6 bg-white-whiter dark:bg-dark-bg min-h-screen">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2 text-salBlue">
            <FiCompass size={24}/>
            {/* Update header title to show city name */}
            <h1 className="text-2xl font-semibold text-text dark:text-dark-text">Roteirização Varginha</h1>
          </div>
          <button onClick={() => { setViewBase(null); setFinalJson(null); }}
                  className="text-gray-600 dark:text-dark-textSecondary hover:text-red-500">
            <FaTimes size={20}/>
          </button>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Rotas Panel - Updated to match POA menu */}
          <div className="bg-white-whiter dark:bg-dark-surface rounded-lg shadow dark:shadow-dark-surface/20 overflow-hidden">
            <div className="px-4 py-3 bg-salBlue text-white-whiter font-medium flex items-center space-x-2">
              <FiMapPin/> <span>Rotas (Sal Express)</span>
            </div>
            <div className="p-4 max-h-[75vh] overflow-y-auto">
              {finalJson?.groupedRoutes
                ? <GroupedRoutesDropdown groupedRoutes={finalJson.groupedRoutes}/>
                : <p className="text-gray-500 dark:text-dark-textSecondary">Rotas Sal Express serão exibidas aqui.</p>}
            </div>
          </div>
          {/* Sumário Panel - Updated to match POA menu */}
          <div className="bg-white-whiter dark:bg-dark-surface rounded-lg shadow dark:shadow-dark-surface/20 overflow-hidden">
            <div className="px-4 py-3 bg-green-500 text-white-whiter font-medium flex items-center space-x-2">
              <FiTrendingUp/> <span>Sumário (Sal Express)</span>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto space-y-4 dark:text-white-whiter">
              <h3 className="text-lg font-semibold">Resumo rotas</h3>
              {finalJson?.totalRotas ? (
                <table
                  id="summary-table-sal-express"
                  className="min-w-full divide-y divide-gray-200 dark:divide-dark-border mt-4 text-sm bg-white-whither dark:bg-dark-surface"
                >
                  <thead className="bg-blue-100 dark:bg-dark-card">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Nome</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Entregas</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Peso</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Frete</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white-whither dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
                    {/* ...existing code for totalRotas... */}
                    {Object.entries(finalJson.totalRotas).map(([routeKey, route]) => (
                      <React.Fragment key={routeKey}>
                        <tr className="bg-gray-50 dark:bg-dark-card font-bold">
                          <td className="px-4 py-2 text-text dark:text-dark-text">
                            <strong>{route.nome}</strong> <span className="font-normal">({route.veiculos})</span>
                          </td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.entregas)}</td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.peso)}</td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.frete)}</td>
                        </tr>
                        {route.cidades && Object.entries(route.cidades).map(([city, data]) => (
                          <tr key={city} className="hover:bg-gray-50 dark:hover:bg-dark-card">
                            <td className="px-4 py-2 pl-8 text-text dark:text-dark-text">{city}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.entregas)}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.peso)}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.frete)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500 dark:text-dark-textSecondary">Nenhum dado de rotas disponível.</p>
              )}
              <h3 className="text-lg font-semibold dark:text-white-whiter">Total Geral</h3>
              <SummaryTable summary={finalJson?.summary}/>
              <div className="flex flex-col space-y-2 mt-3">
                {/* Replace the commented-out download buttons with these for VGA */}
                <a href="https://romaneio.salexpress.com.br/roterizacao/download.php?file=relatorio2&opcao=VGA" download
                   className="flex items-center space-x-2 bg-blue-500 text-white-whiter py-2 px-4 rounded hover:bg-blue-600">
                   <FiDownload/> <span>Baixar Rotas (CSV)</span>
                </a>
                <a href="https://romaneio.salexpress.com.br/roterizacao/download.php?file=relatorio1&opcao=VGA" download
                   className="flex items-center space-x-2 bg-green-500 text-white-whiter py-2 px-4 rounded hover:bg-green-600">
                   <FiFileText/> <span>Baixar Resumo (MD)</span>
                </a>
                <button onClick={handleDownloadSummaryImage} className="flex items-center space-x-2 bg-purple-500 text-white-whiter py-2 px-4 rounded hover:bg-purple-600 focus:outline-none">
                  <FiDownload/> <span>Baixar Sumário como Imagem</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <footer className="mt-6 text-sm text-gray-500 dark:text-dark-textSecondary text-right">
          {lastRunSal ? `Última rota realizada: ${lastRunSal}` : "Carregando última rota..."}
        </footer>
      </section>
    );
  }

  // ViewMode POA
  if (viewBase === "POA") {
    return (
      <section className="p-6 bg-white-whiter dark:bg-dark-bg min-h-screen">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2 text-salBlue">
            <FiCompass size={24}/>
            {/* Update header title to show city name */}
            <h1 className="text-2xl font-semibold text-text dark:text-dark-text">Roteirização Pouso Alegre</h1>
          </div>
          <button onClick={() => { setViewBase(null); setFinalJsonSal(null); }}
                  className="text-gray-600 dark:text-dark-textSecondary hover:text-red-500">
            <FaTimes size={20}/>
          </button>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Rotas Sal */}
          <div className="bg-white-whiter dark:bg-dark-surface rounded-lg shadow dark:shadow-dark-surface/20 overflow-hidden">
            <div className="px-4 py-3 bg-salBlue text-white-whiter font-medium flex items-center space-x-2">
              <FiMapPin/> <span>Rotas (Sal Express)</span>
            </div>
            <div className="p-4 max-h-[75vh] overflow-y-auto">
              {finalJsonSal?.groupedRoutes
                ? <GroupedRoutesDropdown groupedRoutes={finalJsonSal.groupedRoutes}/>
                : <p className="text-gray-500 dark:text-dark-textSecondary">Rotas Sal Express aqui.</p>}
            </div>
          </div>
          {/* Sumário Sal */}
          <div className="bg-white-whiter dark:bg-dark-surface rounded-lg shadow dark:shadow-dark-surface/20 overflow-hidden">
            <div className="px-4 py-3 bg-green-500 text-white-whiter font-medium flex items-center space-x-2">
              <FiTrendingUp/> <span>Sumário (Sal Express)</span>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto space-y-4">
              <h3 className="text-lg font-semibold text-text dark:text-dark-text">Resumo rotas</h3>
              {finalJsonSal?.totalRotas ? (
                <table
                  id="summary-table-sal-express"
                  className="min-w-full divide-y divide-gray-200 dark:divide-dark-border mt-4 text-sm bg-white-whither dark:bg-dark-surface"
                >
                  <thead className="bg-blue-100 dark:bg-dark-card">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Nome</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Entregas</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Peso</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Frete</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white-whither dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
                    {Object.entries(finalJsonSal.totalRotas).map(([routeKey, route]) => (
                      <React.Fragment key={routeKey}>
                        <tr className="bg-gray-50 dark:bg-dark-card font-bold">
                          <td className="px-4 py-2 text-text dark:text-dark-text">
                            <strong>{route.nome}</strong> <span className="font-normal">({route.veiculos})</span>
                          </td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.entregas)}</td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.peso)}</td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.frete)}</td>
                        </tr>
                        {route.cidades && Object.entries(route.cidades).map(([city, data]) => (
                          <tr key={city} className="hover:bg-gray-50 dark:hover:bg-dark-card">
                            <td className="px-4 py-2 pl-8 text-text dark:text-dark-text">{city}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.entregas)}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.peso)}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.frete)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500 dark:text-dark-textSecondary">Nenhum dado de rotas disponível.</p>
              )}

              {/* restore summary geral */}
              <h3 className="text-lg font-semibold text-text dark:text-dark-text">Total Geral</h3>
              <SummaryTable summary={finalJsonSal?.summary}/>

              <div className="flex flex-col space-y-2 mt-3">
                <a href="https://romaneio.salexpress.com.br/roterizacao/download.php?file=relatorio2&opcao=VGA" download
                   className="flex items-center space-x-2 bg-blue-500 text-white-whiter py-2 px-4 rounded hover:bg-blue-600">
                   <FiDownload/> <span>Baixar Rotas (CSV)</span>
                </a>
                <a href="https://romaneio.salexpress.com.br/roterizacao/download.php?file=relatorio1&opcao=VGA" download
                   className="flex items-center space-x-2 bg-green-500 text-white-whiter py-2 px-4 rounded hover:bg-green-600">
                   <FiFileText/> <span>Baixar Resumo (MD)</span>
                </a>
                <button onClick={handleDownloadSummaryImage} className="flex items-center space-x-2 bg-purple-500 text-white-whiter py-2 px-4 rounded hover:bg-purple-600 focus:outline-none">
                  <FiDownload/> <span>Baixar Sumário como Imagem</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* data no rodapé */}
        <footer className="mt-6 text-sm text-gray-500 dark:text-dark-textSecondary text-right">
          {lastRunSal ? `Última rota realizada: ${lastRunSal}` : "Carregando última rota..."}
        </footer>
      </section>
    );
  }

  // ViewMode DIE
  if (viewBase === "DIE") {
    return (
      <section className="p-6 bg-white-whiter dark:bg-dark-bg min-h-screen">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2 text-salBlue">
            <FiCompass size={24}/>
            <h1 className="text-2xl font-semibold text-text dark:text-dark-text">Roteirização Divinópolis</h1>
          </div>
          <button onClick={() => { setViewBase(null); setFinalJsonDie(null); }}
                  className="text-gray-600 dark:text-dark-textSecondary hover:text-red-500">
            <FaTimes size={20}/>
          </button>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Rotas Sal */}
          <div className="bg-white-whiter dark:bg-dark-surface rounded-lg shadow dark:shadow-dark-surface/20 overflow-hidden">
            <div className="px-4 py-3 bg-salBlue text-white-whiter font-medium flex items-center space-x-2">
              <FiMapPin/> <span>Rotas (Sal Express)</span>
            </div>
            <div className="p-4 max-h-[75vh] overflow-y-auto">
              {finalJsonDie?.groupedRoutes
                ? <GroupedRoutesDropdown groupedRoutes={finalJsonDie.groupedRoutes}/>
                : <p className="text-gray-500 dark:text-dark-textSecondary">Rotas Sal Express aqui.</p>}
            </div>
          </div>
          {/* Sumário Sal */}
          <div className="bg-white-whiter dark:bg-dark-surface rounded-lg shadow dark:shadow-dark-surface/20 overflow-hidden">
            <div className="px-4 py-3 bg-green-500 text-white-whiter font-medium flex items-center space-x-2">
              <FiTrendingUp/> <span>Sumário (Sal Express)</span>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto space-y-4">
              <h3 className="text-lg font-semibold text-text dark:text-dark-text">Resumo rotas</h3>
              {finalJsonDie?.totalRotas ? (
                <table
                  id="summary-table-sal-express"
                  className="min-w-full divide-y divide-gray-200 dark:divide-dark-border mt-4 text-sm bg-white-whither dark:bg-dark-surface"
                >
                  <thead className="bg-blue-100 dark:bg-dark-card">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Nome</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Entregas</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Peso</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-dark-text">Frete</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white-whither dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
                    {Object.entries(finalJsonDie.totalRotas).map(([routeKey, route]) => (
                      <React.Fragment key={routeKey}>
                        <tr className="bg-gray-50 dark:bg-dark-card font-bold">
                          <td className="px-4 py-2 text-text dark:text-dark-text">
                            <strong>{route.nome}</strong> <span className="font-normal">({route.veiculos})</span>
                          </td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.entregas)}</td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.peso)}</td>
                          <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(route.total.frete)}</td>
                        </tr>
                        {route.cidades && Object.entries(route.cidades).map(([city, data]) => (
                          <tr key={city} className="hover:bg-gray-50 dark:hover:bg-dark-card">
                            <td className="px-4 py-2 pl-8 text-text dark:text-dark-text">{city}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.entregas)}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.peso)}</td>
                            <td className="px-4 py-2 text-text dark:text-dark-text">{roundValue(data.frete)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500 dark:text-dark-textSecondary">Nenhum dado de rotas disponível.</p>
              )}

              <h3 className="text-lg font-semibold text-text dark:text-dark-text">Total Geral</h3>
              <SummaryTable summary={finalJsonDie?.summary}/>

              <div className="flex flex-col space-y-2 mt-3">
                <a href="https://romaneio.salexpress.com.br/roterizacao/download.php?file=relatorio2&opcao=DIE" download
                   className="flex items-center space-x-2 bg-blue-500 text-white-whiter py-2 px-4 rounded hover:bg-blue-600">
                   <FiDownload/> <span>Baixar Rotas (CSV)</span>
                </a>
                <a href="https://romaneio.salexpress.com.br/roterizacao/download.php?file=relatorio1&opcao=DIE" download
                   className="flex items-center space-x-2 bg-green-500 text-white-whiter py-2 px-4 rounded hover:bg-green-600">
                   <FiFileText/> <span>Baixar Resumo (MD)</span>
                </a>
                <button onClick={handleDownloadSummaryImage} className="flex items-center space-x-2 bg-purple-500 text-white-whiter py-2 px-4 rounded hover:bg-purple-600 focus:outline-none">
                  <FiDownload/> <span>Baixar Sumário como Imagem</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <footer className="mt-6 text-sm text-gray-500 dark:text-dark-textSecondary text-right">
          {lastRunDie ? `Última rota realizada: ${lastRunDie}` : "Carregando última rota..."}
        </footer>
      </section>
    );
  }
};

export default Rastreamento;
